import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { Box, Button, CircularProgress, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import TopNav from '../common/TopNav';
import Container from '../Elements/Container';

import { getPlanInfo } from '../../services/client';
import {
    getAPIToken,
    saveTokenInLocalStorage,
    saveUserInfoInLocalStorage,
    saveNodesInLocalStorage,
    saveNodesListInLocalStorage,
    saveTagsInLocalStorage,
    deleteDefaultDeviceFromLocalStorage,
} from '../../services/auth';
import { getNodes, getNodesTree, getTags } from '../../services/hierarchy';
import { registerUser } from '../../service_worker/helpers';

import UserContext from '../../context/UserContext';
import NodesContext from '../../context/NodesContext';
import NodesListContext from '../../context/NodesListContext';
import TagsContext from '../../context/TagsContext';

const DEMO_USERS = {
    'smart-plus': {
        username: 'demo.smart.plus@powermate.com',
        password: 'cuentaDemo'
    },
    'home': {
        username: 'demo.home@powermate.com',
        password: 'cuentaDemo'
    },
    'general': {
        username: 'demo@powermate.com',
        password: 'cuentaDemo'
    },
    'pro': {
        username: 'demo.pro@powermate.com',
        password: 'cuentaDemo'
    },
    'retail': {
        username: 'demo.retail@powermate.com',
        password: 'cuentaDemo'
    },
    'subfacturacion': {
        username: 'demo.subfactura@powermate.com',
        password: 'cuentaDemo'
    },
    'gateway': {
        username: 'demo.gateway@powermate.com',
        password: 'cuentaDemo'
    },
};

const useStyles = makeStyles(theme => ({
    root: {
        minHeight: 'calc(100vh - 48px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        backgroundColor: theme.palette.common.white,
    },
    spinner: {
        color: theme.palette.primary.main,
    },
    errorPanel: {
        width: '100%',
        maxWidth: '34rem',
    },
    actions: {
        display: 'flex',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
        },
    },
}));

function DemoUser({ setUser, setRefresh }) {
    const classes = useStyles();
    const history = useHistory();
    const { params_id } = useParams();
    const { setPlan } = useContext(UserContext);
    const { setNodes } = useContext(NodesContext);
    const { setNodesList } = useContext(NodesListContext);
    const { setTags } = useContext(TagsContext);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [retryAttempt, setRetryAttempt] = useState(0);
    const demoUser = useMemo(() => DEMO_USERS[params_id] || null, [params_id]);

    useEffect(() => {
        let isMounted = true;

        const runDemoLogin = async () => {
            if (!demoUser) {
                if (!isMounted) return;
                setLoading(false);
                setErrorMessage('El enlace demo no es valido o ya no esta disponible.');
                return;
            }

            try {
                if (!isMounted) return;
                setLoading(true);
                setErrorMessage('');
                registerUser();
                deleteDefaultDeviceFromLocalStorage();
                const res = await getAPIToken(demoUser);
                if (!isMounted) return;
                saveTokenInLocalStorage(res.data.token);
                setUser(true);
                const [planInfoResponse, nodesTreeResponse, nodesResponse, tagsResponse] = await Promise.all([
                    getPlanInfo(),
                    getNodesTree(),
                    getNodes(),
                    getTags(),
                ]);
                if (!isMounted) return;
                saveUserInfoInLocalStorage(planInfoResponse.data);
                setPlan(planInfoResponse.data);
                setRefresh((prevRefresh) => !prevRefresh);
                saveNodesInLocalStorage(nodesTreeResponse.data);
                setNodes(nodesTreeResponse.data);
                saveNodesListInLocalStorage(nodesResponse.data);
                setNodesList(nodesResponse.data);
                saveTagsInLocalStorage(tagsResponse.data);
                setTags(tagsResponse.data);
                setLoading(false);
                history.push('/');
            } catch (error) {
                console.error(error);
                if (!isMounted) return;
                setLoading(false);
                setErrorMessage(error?.response?.data?.detail || error?.response?.data?.msg || 'No pudimos iniciar la cuenta demo en este momento.');
            }
        };

        runDemoLogin();

        return () => {
            isMounted = false;
        };
    }, [demoUser, history, retryAttempt, setNodes, setNodesList, setPlan, setRefresh, setTags, setUser]);

    const handleRetry = () => {
        setLoading(true);
        setErrorMessage('');
        setRetryAttempt((prev) => prev + 1);
    };

    return (
        <>
            <TopNav titulo='Demo' />
            <Container>
                <Box className={classes.root}>
                    {loading ? (
                        <CircularProgress size={36} className={classes.spinner} />
                    ) : errorMessage ? (
                        <Box className={classes.errorPanel}>
                            <Alert severity='error'>
                                {errorMessage}
                            </Alert>
                            <Box className={classes.actions}>
                                <Button color='primary' variant='contained' onClick={handleRetry}>
                                    Reintentar
                                </Button>
                                <Button color='primary' variant='outlined' onClick={() => history.push('/login')}>
                                    Ir al login
                                </Button>
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            </Container>
        </>
    );
}

export default DemoUser;



